import { useState } from 'react';
import type { Alert } from '../lib/types';

export default function Alerts() {
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');

  // Mock alerts data
  const alerts: Alert[] = [
    {
      id: '1',
      projectId: '1',
      targetId: 'server-01',
      targetType: 'server',
      severity: 'critical',
      message: 'CPU usage exceeds 95%',
      description: 'Server web-01 is experiencing high CPU load',
      createdAt: new Date(Date.now() - 300000),
      acknowledged: false
    },
    {
      id: '2',
      projectId: '1',
      targetId: 'component-05',
      targetType: 'component',
      severity: 'high',
      message: 'Response time degradation detected',
      description: 'API Gateway response time increased by 150%',
      createdAt: new Date(Date.now() - 600000),
      acknowledged: false
    },
    {
      id: '3',
      projectId: '2',
      targetId: 'server-03',
      targetType: 'server',
      severity: 'medium',
      message: 'Disk usage at 85%',
      description: 'Database server disk space running low',
      createdAt: new Date(Date.now() - 1200000),
      acknowledged: true,
      acknowledgedAt: new Date(Date.now() - 600000),
      acknowledgedBy: 'admin@example.com'
    }
  ];

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'low': return 'bg-blue-100 text-blue-700 border-blue-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const filteredAlerts = alerts.filter(alert => {
    const matchesSeverity = selectedSeverity === 'all' || alert.severity === selectedSeverity;
    const matchesStatus = selectedStatus === 'all' ||
      (selectedStatus === 'acknowledged' && alert.acknowledged) ||
      (selectedStatus === 'unacknowledged' && !alert.acknowledged);
    return matchesSeverity && matchesStatus;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Alerts</h1>
            <p className="text-gray-600">Monitor and manage system alerts</p>
          </div>

          <div className="flex items-center space-x-3">
            <select
              value={selectedSeverity}
              onChange={(e) => setSelectedSeverity(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Severities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="unacknowledged">Unacknowledged</option>
              <option value="acknowledged">Acknowledged</option>
            </select>
          </div>
        </div>

        {/* Alert Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {[
            { label: 'Critical', count: alerts.filter(a => a.severity === 'critical').length, color: 'red' },
            { label: 'High', count: alerts.filter(a => a.severity === 'high').length, color: 'orange' },
            { label: 'Medium', count: alerts.filter(a => a.severity === 'medium').length, color: 'yellow' },
            { label: 'Total', count: alerts.length, color: 'blue' }
          ].map(stat => (
            <div key={stat.label} className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="text-sm text-gray-600 mb-1">{stat.label} Alerts</div>
              <div className={`text-3xl font-bold text-${stat.color}-600`}>{stat.count}</div>
            </div>
          ))}
        </div>

        {/* Alerts List */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">
              All Alerts ({filteredAlerts.length})
            </h2>
          </div>

          <div className="divide-y divide-gray-200">
            {filteredAlerts.map(alert => (
              <div key={alert.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getSeverityColor(alert.severity)}`}>
                        {alert.severity.toUpperCase()}
                      </span>
                      <span className="text-sm text-gray-600">
                        {alert.targetType} · {alert.targetId}
                      </span>
                      {alert.acknowledged && (
                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">
                          <i className="ri-check-line mr-1"></i>
                          Acknowledged
                        </span>
                      )}
                    </div>

                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{alert.message}</h3>
                    {alert.description && (
                      <p className="text-gray-600 mb-3">{alert.description}</p>
                    )}

                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <span className="flex items-center">
                        <i className="ri-time-line mr-1"></i>
                        {new Date(alert.createdAt).toLocaleString()}
                      </span>
                      {alert.acknowledgedBy && (
                        <span className="flex items-center">
                          <i className="ri-user-line mr-1"></i>
                          Acknowledged by {alert.acknowledgedBy}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2 ml-4">
                    {!alert.acknowledged && (
                      <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
                        <i className="ri-check-line mr-2"></i>
                        Acknowledge
                      </button>
                    )}
                    <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                      <i className="ri-more-line"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {filteredAlerts.length === 0 && (
              <div className="p-12 text-center">
                <i className="ri-check-double-line text-6xl text-gray-300 mb-4"></i>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No alerts found</h3>
                <p className="text-gray-600">All systems are running smoothly</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
